import { useMemo } from 'react'

import { FiltersDefinition, FiltersState, IconType } from '@factorialco/f0-react'
import { DateRange, OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { AnalyticsSnapshotResultsDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { filterDefinitionsFromSchema } from 'modules/ai_reports/components/DatasourceTypeRenderer/filters'
import { ColumnFormatHint } from 'modules/ai_reports/components/DatasourceTypeRenderer/utils'

import SnapshotTableContent from './SnapshotTableContent'

interface ReportTableAction {
  label: string
  icon: IconType
  onClick: () => void
}

interface ReportTableProps {
  id: string
  reportName: string
  sqlQuery: string
  actions?: {
    primary?: ReportTableAction
    secondary?: ReportTableAction[]
  }
  columnFormatHints: ColumnFormatHint[]
  onStateChange: React.ComponentProps<typeof OneDataCollection>['onStateChange']
  tableId: string
  dateFilter?: DateRange
  onUserFacingSqlQueryChange: (sqlQuery: string) => void
  currentFilters?: FiltersState<FiltersDefinition<string>>
}

const INITIAL_PAGE = 1
const INITIAL_PAGE_SIZE = 10

const ReportTable = ({
  id,
  sqlQuery,
  actions,
  columnFormatHints,
  onStateChange,
  tableId,
  dateFilter,
  onUserFacingSqlQueryChange,
  currentFilters,
}: ReportTableProps) => {
  const { company } = useCurrentSession()
  const dateNow = useMemo(() => new Date().toISOString(), [])

  // Used to fetch the columns.
  const { data: columnsData } = useQuery(AnalyticsSnapshotResultsDocument, {
    variables: {
      query: sqlQuery,
      companyId: company.id,
      snapshotDate: dateNow,
      page: INITIAL_PAGE,
      perPage: INITIAL_PAGE_SIZE,
      tableId: tableId,
    },
  })

  // FIXME: The Data Collection filters are not updated unless they are added to
  // the Data Collection source dependencies array. If this is fixed, we can
  // remove the hook and use the filters directly.
  const schema = columnsData?.iris?.snapshotResultsConnection?.nodes?.[0]?.schema

  const filterDefinitions = useMemo(() => filterDefinitionsFromSchema(schema ?? []), [schema])

  if (!schema) {
    return <Loading />
  }

  return (
    <SnapshotTableContent
      id={id}
      sqlQuery={sqlQuery}
      tableId={tableId}
      actions={actions}
      columnFormatHints={columnFormatHints}
      onStateChange={onStateChange}
      dateFilter={dateFilter}
      onUserFacingSqlQueryChange={onUserFacingSqlQueryChange}
      columnsData={columnsData}
      filterDefinitions={filterDefinitions}
      currentFilters={currentFilters}
    />
  )
}

export default ReportTable
export { type ReportTableAction, type ReportTableProps }
